import React, {Component} from "react";
import {
  Grid,
  TableCell,
  TableHead,
  TableRow,
  Table,
  TableBody,
  Paper,
  styled,
  Box
} from "@mui/material";
import {
  PieChart, Pie, Tooltip,
  ResponsiveContainer,
  RadialBarChart, RadialBar
} from 'recharts';
import {DataSetInstallStatistic, Statistics, UpdateTriggerStatistic} from "./model";
import {Progress} from "./Progress";
import axios from "axios";

export interface DashboardPageProps {

}

export interface DashboardPageState {
  statistics: Statistics | null;
  loading: boolean;
  failed: boolean;
  selected_table_entry: string | null;
}

// Styled components
const StyledInnerContentRoot = styled(Box)(() => ({
  marginLeft: 'auto',
  marginRight: 'auto',
  marginTop: 10,
  maxWidth: 1200,
}));

const StyledInnerContentPaper = styled(Paper)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: '0px -1px 2px 0px rgba(0,0,0,0.1), 0px 2px 2px 0px rgba(0,0,0,0.1), 0px 1px 1px -2px rgba(0,0,0,0.1)',
}));

const StyledTableCell = styled(TableCell)(() => ({
  cursor: 'pointer',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '0.9375em'
}));

const StyledTableCellBold = styled(TableCell)(() => ({
  cursor: 'pointer',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '0.9375em',
  fontWeight: 500
}));

const StyledTableCellPlaceholder = styled(TableCell)(() => ({
  textAlign: 'center',
  fontSize: '0.9375em'
}));

const StyledHeaderLeft = styled(Grid)(({ theme }) => ({
  marginLeft: '24px',
  marginTop: '10px',
}));

const StyledHeaderLabel = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 500,
  fontSize: '1.05em'
}));

const StyledError = styled(Box)(({ theme }) => ({
  color: theme.palette.error.contrastText,
  marginTop: 1,
  marginBottom: 10,
  background: theme.palette.error.main,
  fontSize: '0.9375em',
  padding: 5,
  height: 70,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center'
}));

const StyledFillingChartContainer = styled(Grid)(() => ({
  flexGrow: 1,
  alignSelf: 'stretch'
}));


class DashboardPage extends Component<DashboardPageProps, DashboardPageState> {

  state = {
    statistics: null,
    loading: true,
    failed: false,
    selected_table_entry: null
  };

  async fetchStatistics() {
    this.setState({loading: true, failed: false});
    try {
      let response = await axios.get('/api/admin/statistics');
      this.setState({loading: false, failed: false, statistics: response.data});
    } catch(error) {
      this.setState({loading: false, failed: true, statistics: null});
    }
  }
  componentDidMount(): void {
    this.fetchStatistics();
  }

  formatAsPercentage(value: number, total: number): string {
    if (value && total && total > 0) {
      return `${((value) / total * 100).toLocaleString('de-AT', {
        minimumIntegerDigits: 1,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}%`;
    }
    return "-";
  }

  renderDeviceStatistics() {
    let data : any = this.state.statistics;
    let datasetInstalls: DataSetInstallStatistic[] = data.dataset_installs;

    let currentSets = datasetInstalls.filter(value => value.is_current_dataset);
    let devicesWithCurrentDataset = 0;
    if (currentSets.length > 0) {
      devicesWithCurrentDataset = currentSets[0].active_device_count;
    }

    const chartData = [
      { name: 'Nicht authorisierte Geräte in den letzten 30 Tagen', value: data.unauthorized_device_count_30d, fill: '#d02321', push_identifier: 'unauthorized_devices_30d' },
      { name: 'Nicht authorisierte Geräte', value: data.unauthorized_device_count_current, fill: '#d02321', push_identifier: 'unauthorized_devices' },
      { name: 'Geräte mit aktuellem Datensatz', value: devicesWithCurrentDataset, fill: '#1abe53', push_identifier: 'updated_devices' },
      { name: 'Synchronisierte Geräte', value: data.total_device_count - data.unauthorized_device_count_current, fill: '#BCE4FA', push_identifier: 'synchronized_devices' },
      { name: 'Registrierte Geräte', value: data.total_device_count, fill: '#005B94', push_identifier: 'registered_devices' },
      { name: 'Geräte ohne Update-Anfrage in den letzten 30 Tagen', value: data.older_than_30d_device_count, fill: '#ff7900', push_identifier: 'unseen_devices' }
    ];

    return (
      <Grid container direction={"row"} alignItems={"center"}>
        <StyledHeaderLeft item xs={12}>
          <StyledHeaderLabel>
            Geräte
          </StyledHeaderLabel>
        </StyledHeaderLeft>

        <Grid item xs={8}>
          <StyledInnerContentPaper>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding={"none"}>Bezeichnung</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte in %</StyledTableCell>
                  <TableCell style={{width: '5px'}}/>
                </TableRow>
              </TableHead>
              <TableBody>
                { chartData.reverse().map(value => {
                    return (
                        <TableRow hover onClick={(event) => {this.setState({selected_table_entry: value.push_identifier});}} key={value.push_identifier} selected={this.state.selected_table_entry === value.push_identifier}>
                          <StyledTableCell style={{borderLeft: `4px solid ${value.fill}`}} scope="row">
                            {value.name}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {value.value}
                          </StyledTableCell>
                          <StyledTableCell align="right">
                            {this.formatAsPercentage(value.value, data.total_device_count)}
                          </StyledTableCell>
                          <TableCell/>
                        </TableRow>
                    );
                  })
                }
              </TableBody>
            </Table>
          </StyledInnerContentPaper>
        </Grid>
        <StyledFillingChartContainer item xs={4}>
          <div style={{ width: '100%', height: '100%'}}>
            <ResponsiveContainer height='100%' width='100%'>
              <RadialBarChart startAngle={90} endAngle={-270} innerRadius={20} data={chartData} outerRadius={'100%'} >
                <RadialBar label={{ position: 'insideStart', fill: '#fff' }} background dataKey="value" isAnimationActive={false}/>
                <Tooltip labelFormatter={(index) => `${chartData[index as number].name}`} />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </StyledFillingChartContainer>
      </Grid>
    );
  }

  renderDatasetStatistics() {
    let data : any = this.state.statistics;
    let datasetInstalls: DataSetInstallStatistic[] = data.dataset_installs;

    let oldColors = ['#ff7900','#ff8900','#ff9900','#ffa900','#ffb900','#ffc900','#ffd900'];
    let oldColorIndex = 0;

    const chartData = datasetInstalls.map(value => {
      if (value.is_current_dataset) {
        return { name: `Aktiver Datensatz ${value.visible_identifier}`, value: value.active_device_count, fill: '#1abe53', is_current: true, push_identifier: 'dataset_' + value.identifier }
      } else if (value.identifier) {
        if (oldColorIndex >= oldColors.length) {
          oldColorIndex = 0;
        }
        let color = oldColors[oldColorIndex];
        oldColorIndex += 1;
        return { name: `Veralteter Datensatz ${value.visible_identifier}`, value: value.active_device_count, fill: color, is_current: false, push_identifier: 'dataset_' + value.identifier}
      } else {
        return { name: `Ohne Datensatz`, value: value.active_device_count, fill: '#d02321', is_current: false, push_identifier: 'dataset_missing'}
      }
    });
    let totalDeviceCount = data.total_device_count;

    return (
      <Grid container direction={"row"} alignItems={"center"}>
        <StyledHeaderLeft item xs={12}>
          <StyledHeaderLabel>
            Datensätze
          </StyledHeaderLabel>
        </StyledHeaderLeft>
        <Grid item xs={8}>
          <StyledInnerContentPaper>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding={"none"}>Datensatz</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte absolut</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte in %</StyledTableCell>
                  <TableCell style={{width: '5px'}}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {chartData.map(row => {
                  const CellComponent = row.is_current ? StyledTableCellBold : StyledTableCell;

                  return (
                    <TableRow hover onClick={(event) => {this.setState({selected_table_entry: row.push_identifier});}} key={row.push_identifier} selected={this.state.selected_table_entry === row.push_identifier}>
                      <CellComponent style={{borderLeft: `4px solid ${row.fill}`}} scope="row">
                        {row.name}
                      </CellComponent>
                      <CellComponent align="right">
                        {row.value} von {totalDeviceCount}
                      </CellComponent>
                      <CellComponent align="right">
                        {this.formatAsPercentage(row.value, totalDeviceCount)}
                      </CellComponent>
                      <TableCell/>
                    </TableRow>
                  );
                })}
                {datasetInstalls.length === 0 &&
                  <TableRow>
                    <StyledTableCellPlaceholder colSpan={4}>
                      Noch keine Datensätze erfasst
                    </StyledTableCellPlaceholder>
                  </TableRow>
                }
              </TableBody>
            </Table>
          </StyledInnerContentPaper>
        </Grid>
        <StyledFillingChartContainer item xs={4}>
          <div style={{ width: '100%', height: '100%'}}>
            <ResponsiveContainer height='100%' width='100%'>
              <PieChart>
                <Pie startAngle={90} endAngle={-270} dataKey="value" data={chartData} fill="#005B94" isAnimationActive={false}/>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </StyledFillingChartContainer>
      </Grid>
    );
  }

  renderUpdateTriggerStatistics() {
    let data : any = this.state.statistics;
    let updateTriggers: UpdateTriggerStatistic[] = data.update_triggers;

    let totalDeviceCount = data.total_device_count;
    const chartData = updateTriggers.map(value => {
      if (value.trigger_type) {
        if (value.trigger_type === 'app_start') {
          return { name: "App-Start", value: value.active_device_count, fill: '#005B94', push_identifier: 'app_start'};
        } else if (value.trigger_type === 'background_sync') {
          return { name: "Hintergrundaktualisierung", value: value.active_device_count, fill: '#BCE4FA', push_identifier: 'background_sync'};
        } else if (value.trigger_type === 'push') {
          return { name: "Push-Benachrichtigung", value: value.active_device_count, fill: '#313a46', push_identifier: 'push'};
        } else if (value.trigger_type === 'user_triggered') {
          return { name: "Manuell durch Benutzer", value: value.active_device_count, fill: '#313a46', push_identifier: 'user_triggered'};
        } else {
          return { name: "Unbekannt", value: value.active_device_count, fill: '#ff7900', push_identifier: 'unknown'};
        }
      } else {
        return { name: `Nicht synchronisiert`, value: value.active_device_count, fill: '#d02321', push_identifier: 'unsynced'}
      }
    });

    return (
      <Grid container direction={"row"} alignItems={"center"}>
        <StyledHeaderLeft item xs={12}>
          <StyledHeaderLabel>
            Update-Auslöser
          </StyledHeaderLabel>
        </StyledHeaderLeft>
        <Grid item xs={8}>
          <StyledInnerContentPaper>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell padding={"none"}>Auslöser</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte absolut</StyledTableCell>
                  <StyledTableCell padding={"none"} align="right">Geräte in %</StyledTableCell>
                  <TableCell style={{width: '5px'}}/>
                </TableRow>
              </TableHead>
              <TableBody>
                { chartData.map(row => {

                  return (
                      <TableRow hover onClick={(event) => {this.setState({selected_table_entry: row.push_identifier});}} key={row.push_identifier} selected={this.state.selected_table_entry === row.push_identifier}>
                        <StyledTableCell style={{borderLeft: `4px solid ${row.fill}`}} scope="row">
                        {row.name}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {row.value} von {totalDeviceCount}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {this.formatAsPercentage(row.value, totalDeviceCount)}
                      </StyledTableCell>
                      <TableCell/>
                    </TableRow>
                  );
                })}
                {updateTriggers.length === 0 &&
                <TableRow>
                  <StyledTableCellPlaceholder colSpan={4}>
                    Noch keine Datensätze erfasst
                  </StyledTableCellPlaceholder>
                </TableRow>
                }
              </TableBody>
            </Table>
          </StyledInnerContentPaper>
        </Grid>
        <StyledFillingChartContainer item xs={4}>
          <div style={{ width: '100%', height: '100%'}}>
            <ResponsiveContainer height='100%' width='100%'>
              <PieChart >
                <Pie startAngle={90} endAngle={-270} dataKey="value" data={chartData} fill="#005B94" isAnimationActive={false}/>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </StyledFillingChartContainer>
      </Grid>
    );
  }

  render() {
    if (this.state.failed) {
      return (
        <StyledInnerContentRoot>
          <StyledError>Fehler beim Laden des Dashboards</StyledError>
        </StyledInnerContentRoot>
      );
    } else if (this.state.loading) {
      return (
        <Progress/>
      );
    }

    return (
      <StyledInnerContentRoot>

        {this.renderDeviceStatistics()}
        {this.renderDatasetStatistics()}
        {this.renderUpdateTriggerStatistics()}
      </StyledInnerContentRoot>
    );
  }
}

export default DashboardPage;
